import { useState, useCallback, useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';
import { useRoomStore, loadGameCache, clearGameCache, maskIdentities } from '../../store/roomStore';
import { CHARACTERS } from '../../data';
import { ASSETS, getCharSelectPath } from '../../data/assets';
import { requestFullscreen, useMobileDetect } from '../../hooks/useMobileDetect';
import { shuffle } from '../../utils/shuffle';
import type { RoomSnapshot } from '../../types';
import styles from './Lobby.module.css';

const BOT_NAMES = ['小明', '小红', '大壮', '小美', '阿强', '丽丽'];

/** 随机昵称生成 — 可爱形容词 + 动物/物品 */
function generateRandomName(): string {
  const adjectives = ['闪电', '星光', '风暴', '烈焰', '寒冰', '幻影', '旋风', '雷霆', '极光', '暗影', '彩虹', '银河', '流星', '疾风', '飞龙', '晶石', '灵猫', '银狼', '炎雀', '冰凰'];
  const nouns = ['兔', '猫', '狐', '狼', '鹰', '鲸', '鹿', '雀', '龙', '凤', '熊', '豹', '鹤', '隼', '蝶', '鱼', '马', '牛', '羊', '蛇'];
  return adjectives[Math.floor(Math.random() * adjectives.length)] + nouns[Math.floor(Math.random() * nouns.length)];
}

/** 检查玩家名是否与房间内已连接玩家重名 */
function isNameTaken(name: string, snapshot: RoomSnapshot | null): boolean {
  if (!snapshot) return false;
  return snapshot.players.some(
    p => p.name.trim().toLowerCase() === name.trim().toLowerCase() && !p.disconnected
  );
}

type LobbyMode = 'home' | 'offline' | 'online-create' | 'online-join' | 'online-waiting' | 'ready';

export default function Lobby() {
  const { addPlayer, initGame } = useGameStore();
  const { createRoom, joinRoom, startGame: startOnlineGame } = useRoomStore();

  const [lobbyMode, setLobbyMode] = useState<LobbyMode>('home');
  const [playerName, setPlayerName] = useState(() => generateRandomName());
  const [selectedChar, setSelectedChar] = useState(CHARACTERS[0].id);
  const [playerCount, setPlayerCount] = useState(4);

  // 联机相关
  const [joinRoomId, setJoinRoomId] = useState('');
  const [onlineError, setOnlineError] = useState('');
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [copiedRoomId, setCopiedRoomId] = useState(false);

  const roomStore = useRoomStore();
  const roomSnapshot: RoomSnapshot | null = roomStore.roomSnapshot;
  const myRoomId = roomStore.roomId;
  const isHost = roomStore._isHost;
  const { isMobile } = useMobileDetect();

  // ─── 自动重连检测 ─────────────────────────────────────────
  useEffect(() => {
    // PC端：页面加载后检测是否有缓存的联机游戏
    if (isMobile) return; // 移动端暂不自动重连（浏览器可能被杀进程）
    const cache = loadGameCache();
    if (!cache || cache.phase === 'gameOver') {
      // 清理过期缓存
      if (cache) clearGameCache();
      return;
    }
    // 自动重连
    console.log('[lobby] auto-reconnecting from cache:', cache);
    setOnlineLoading(true);
    setPlayerName(cache.myPlayerName);
    setLobbyMode('online-join');

    joinRoom(cache.roomId, cache.myPlayerName, cache.myCharacterId || CHARACTERS[0].id).then((res) => {
      setOnlineLoading(false);
      if (res.ok && res.reconnected) {
        // 重连成功：用服务器 gameState 恢复（屏蔽未暴露身份）
        if (res.snapshot?.gameState) {
          const maskedState = maskIdentities(res.snapshot.gameState, cache.myPlayerName);
          useGameStore.setState(maskedState);
        }
        // 游戏进行中 → 直接进入游戏
        if (res.snapshot?.phase === 'playing') {
          if (isMobile) requestFullscreen();
        }
        // 更新缓存
        // saveGameCache already called in roomStore's joinRoom success path
      } else if (!res.ok) {
        // 房间已失效
        clearGameCache();
        setOnlineError('缓存的游戏已结束');
        setLobbyMode('home');
      } else {
        // 新加入（非重连），进入等待
        setLobbyMode('online-waiting');
      }
    }).catch(() => {
      setOnlineLoading(false);
      clearGameCache();
      setLobbyMode('home');
    });
  }, []); // 仅首次挂载执行

  // ─── 本地模式 ─────────────────────────────────────────────
  const handleOfflineStart = () => {
    initGame(playerCount);
    addPlayer(playerName || '大佬米', selectedChar, false);
    for (let i = 1; i < playerCount; i++) {
      const botChar = CHARACTERS[i % CHARACTERS.length].id;
      addPlayer(BOT_NAMES[i - 1] || `玩家${i + 1}`, botChar, true);
    }
    useGameStore.getState().assignIdentities();
    setLobbyMode('ready');
  };

  const handleGoGame = () => {
    if (isMobile) requestFullscreen();
    useGameStore.getState().startGame();
  };

  // ─── 联机：创建房间 ────────────────────────────────────────
  const handleCreateRoom = useCallback(async () => {
    if (!playerName.trim()) { setOnlineError('请输入你的昵称'); return; }
    setOnlineError('');
    setOnlineLoading(true);
    const res = await createRoom(playerName.trim(), selectedChar);
    setOnlineLoading(false);
    if (res.ok) {
      setLobbyMode('online-waiting');
    } else {
      setOnlineError(res.error || '创建房间失败');
    }
  }, [playerName, selectedChar, createRoom]);

  // ─── 联机：加入房间 ────────────────────────────────────────
  const handleJoinRoom = useCallback(async () => {
    if (!playerName.trim()) { setOnlineError('请输入你的昵称'); return; }
    if (!joinRoomId.trim()) { setOnlineError('请输入房间号'); return; }
    setOnlineError('');
    setOnlineLoading(true);
    const res = await joinRoom(joinRoomId.trim().toUpperCase(), playerName.trim(), selectedChar);
    setOnlineLoading(false);
    if (res.ok) {
      if (res.reconnected) {
        // 重连：用服务器 gameState 恢复本地状态（屏蔽未暴露身份）
        if (res.snapshot?.gameState) {
          const maskedState = maskIdentities(res.snapshot.gameState, playerName.trim());
          useGameStore.setState(maskedState);
        }
        // 游戏进行中 → 直接全屏进入
        if (res.snapshot?.phase === 'playing') {
          if (isMobile) requestFullscreen();
        }
      } else {
        setLobbyMode('online-waiting');
      }
    } else {
      setOnlineError(res.error || '加入房间失败');
    }
  }, [playerName, joinRoomId, selectedChar, joinRoom, isMobile]);

  // ─── 联机：房主开始游戏 ────────────────────────────────────
  const handleOnlineStart = () => {
    if (!roomSnapshot) return;
    // 1. 初始化本地 gameStore（房主负责初始化游戏状态）
    const players = roomSnapshot.players.filter(p => !p.isBot);
    const totalCount = Math.max(players.length, 2);
    initGame(totalCount);
    players.forEach((p, i) => {
      addPlayer(p.name, p.characterId, false);
    });
    // 补足 Bot
    const bots = totalCount - players.length;
    for (let i = 0; i < bots; i++) {
      const botChar = CHARACTERS[(players.length + i) % CHARACTERS.length].id;
      addPlayer(BOT_NAMES[i] || `玩家Bot${i + 1}`, botChar, true);
    }
    useGameStore.getState().assignIdentities();
    // 2. 服务端权威：由服务端创建游戏并广播 game:state
    // 3. 通知服务端开始游戏
    startOnlineGame();
    // 4. 服务端会广播 room:started + game:state，客户端接收后自动初始化游戏界面
    // 此处预切 phase 确保 GameBoard 即时挂载
    useGameStore.getState().startGame();
    if (isMobile) requestFullscreen();
  };

  // ─── 复制房间号 ────────────────────────────────────────────
  const handleCopyRoomId = () => {
    if (!myRoomId) return;
    navigator.clipboard.writeText(myRoomId).then(() => {
      setCopiedRoomId(true);
      setTimeout(() => setCopiedRoomId(false), 2000);
    });
  };

  const currentPlayers = useGameStore.getState().players;

  return (
    <div className={styles.container}>
      <div className={styles.bgDecor}>
        <div className={styles.bgCircle1} />
        <div className={styles.bgCircle2} />
        <div className={styles.bgCircle3} />
      </div>

      <div className={styles.content}>
        {/* Logo */}
        <div className={styles.logoSection}>
          <img src={ASSETS.ui.logo} alt="翻转大富翁" className={styles.logoImg} />
          <p className={styles.subtitle}>每一轮都可能天翻地覆 — 玩大富翁不靠运气靠翻转</p>
        </div>

        <div className={styles.configPanel}>

          {/* ══════════ HOME ══════════ */}
          {lobbyMode === 'home' && (
            <div className={styles.modeSelect}>
              <h2 className={styles.modeTitle}>选择游戏模式</h2>
              <button className={styles.modeBtn} onClick={() => setLobbyMode('offline')}>
                <span className={styles.modeBtnIcon}>🖥️</span>
                <div>
                  <div className={styles.modeBtnLabel}>本地模式</div>
                  <div className={styles.modeBtnDesc}>与 AI 对战，单机游玩</div>
                </div>
              </button>
              <button className={`${styles.modeBtn} ${styles.modeBtnOnline}`} onClick={() => setLobbyMode('online-create')}>
                <span className={styles.modeBtnIcon}>🌐</span>
                <div>
                  <div className={styles.modeBtnLabel}>联机模式</div>
                  <div className={styles.modeBtnDesc}>创建或加入房间，与朋友联机</div>
                </div>
              </button>
            </div>
          )}

          {/* ══════════ 本地模式配置 ══════════ */}
          {lobbyMode === 'offline' && (
            <>
              <div className={styles.sectionHeader}>
                <button className={styles.headerBackBtn} onClick={() => setLobbyMode('home')}>← 返回</button>
                <span className={styles.sectionTitle}>🖥️ 本地模式</span>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>你的昵称</label>
                <input className={styles.input} value={playerName} onChange={e => setPlayerName(e.target.value)} placeholder="输入你的名字" maxLength={8} />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>选择角色</label>
                <div className={styles.charGrid}>
                  {CHARACTERS.map(char => (
                    <button key={char.id} className={`${styles.charBtn} ${selectedChar === char.id ? styles.charBtnActive : ''}`} onClick={() => setSelectedChar(char.id)}>
                      <img src={getCharSelectPath(char.id)} alt={char.name} className={styles.charImg} />
                      <span className={styles.charName}>{char.name}</span>
                      <span className={styles.charSkill}>{char.passiveDesc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>玩家数量：<span className={styles.countBadge}>{playerCount}人</span></label>
                <div className={styles.countBtns}>
                  {[2, 3, 4].map(n => (
                    <button key={n} className={`${styles.countBtn} ${playerCount === n ? styles.countBtnActive : ''}`} onClick={() => setPlayerCount(n)}>{n}人</button>
                  ))}
                </div>
              </div>

              <button className={styles.startBtn} onClick={handleOfflineStart}>
                <span>⚡</span> 开始游戏
              </button>
            </>
          )}

          {/* ══════════ 联机 · 创建房间 ══════════ */}
          {lobbyMode === 'online-create' && (
            <>
              <div className={styles.sectionHeader}>
                <button className={styles.headerBackBtn} onClick={() => { setLobbyMode('home'); setOnlineError(''); }}>← 返回</button>
                <span className={styles.sectionTitle}>🌐 联机模式</span>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>你的昵称</label>
                <div className={styles.nameRow}>
                  <input className={styles.input} value={playerName} onChange={e => setPlayerName(e.target.value)} placeholder="输入你的名字" maxLength={8} />
                  <button className={styles.shuffleNameBtn} onClick={() => setPlayerName(generateRandomName())} title="换一个随机昵称">🎲</button>
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>选择角色</label>
                <div className={styles.charGrid}>
                  {CHARACTERS.map(char => (
                    <button key={char.id} className={`${styles.charBtn} ${selectedChar === char.id ? styles.charBtnActive : ''}`} onClick={() => setSelectedChar(char.id)}>
                      <img src={getCharSelectPath(char.id)} alt={char.name} className={styles.charImg} />
                      <span className={styles.charName}>{char.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {onlineError && <div className={styles.errorMsg}>{onlineError}</div>}

              <div className={styles.onlineActions}>
                <button className={styles.startBtn} onClick={handleCreateRoom} disabled={onlineLoading}>
                  {onlineLoading ? '连接中...' : <><span>🏠</span> 创建房间</>}
                </button>
                <div className={styles.divider}><span>或</span></div>
                <div className={styles.joinRow}>
                  <input
                    className={`${styles.input} ${styles.roomCodeInput}`}
                    value={joinRoomId}
                    onChange={e => setJoinRoomId(e.target.value.toUpperCase())}
                    placeholder="输入6位房间号"
                    maxLength={6}
                  />
                  <button className={styles.joinBtn} onClick={() => { setOnlineError(''); setLobbyMode('online-join'); }} disabled={!joinRoomId.trim()}>
                    加入 →
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ══════════ 联机 · 输入房间号加入 ══════════ */}
          {lobbyMode === 'online-join' && (
            <>
              <div className={styles.sectionHeader}>
                <button className={styles.headerBackBtn} onClick={() => { setLobbyMode('online-create'); setOnlineError(''); }}>← 返回</button>
                <span className={styles.sectionTitle}>🚪 加入房间</span>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>你的昵称</label>
                <div className={styles.nameRow}>
                  <input className={styles.input} value={playerName} onChange={e => setPlayerName(e.target.value)} placeholder="输入你的名字" maxLength={8} />
                  <button className={styles.shuffleNameBtn} onClick={() => setPlayerName(generateRandomName())} title="换一个随机昵称">🎲</button>
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>房间号</label>
                <input
                  className={`${styles.input} ${styles.roomCodeBig}`}
                  value={joinRoomId}
                  onChange={e => setJoinRoomId(e.target.value.toUpperCase())}
                  placeholder="6位房间号"
                  maxLength={6}
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>选择角色</label>
                <div className={styles.charGrid}>
                  {CHARACTERS.map(char => (
                    <button key={char.id} className={`${styles.charBtn} ${selectedChar === char.id ? styles.charBtnActive : ''}`} onClick={() => setSelectedChar(char.id)}>
                      <img src={getCharSelectPath(char.id)} alt={char.name} className={styles.charImg} />
                      <span className={styles.charName}>{char.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {onlineError && <div className={styles.errorMsg}>{onlineError}</div>}

              <button className={styles.startBtn} onClick={handleJoinRoom} disabled={onlineLoading || !joinRoomId.trim()}>
                {onlineLoading ? '加入中...' : <><span>🚪</span> 进入房间</>}
              </button>
            </>
          )}

          {/* ══════════ 联机 · 等待房间（大厅）══════════ */}
          {lobbyMode === 'online-waiting' && roomSnapshot && (
            <>
              <div className={styles.waitingHeader}>
                <span className={styles.waitingTitle}>🏠 等待玩家加入</span>
                {/* 房间号展示 + 复制 */}
                <div className={styles.roomCodeCard}>
                  <span className={styles.roomCodeLabel}>房间号</span>
                  <span className={styles.roomCode}>{myRoomId}</span>
                  <button className={styles.copyBtn} onClick={handleCopyRoomId}>
                    {copiedRoomId ? '✅ 已复制' : '📋 复制'}
                  </button>
                </div>
                <p className={styles.waitingHint}>将房间号发给朋友，等待他们加入</p>
              </div>

              <div className={styles.playerList}>
                {roomSnapshot.players.map((p, i) => (
                  <div key={p.name} className={styles.playerRow}>
                    <img src={getCharSelectPath(p.characterId)} alt={p.name} className={styles.playerRowImg} />
                    <span className={styles.playerTag}>{i === 0 ? '👑 房主' : '🎮 玩家'}</span>
                    <span className={styles.playerName}>{p.name}</span>
                    <span className={styles.playerChar}>{CHARACTERS.find(c => c.id === p.characterId)?.name}</span>
                    <span className={styles.onlineDot} />
                  </div>
                ))}
              </div>

              <div className={styles.waitingActions}>
                {isHost ? (
                  <button
                    className={styles.startBtn}
                    onClick={handleOnlineStart}
                    disabled={roomSnapshot.players.length < 2}
                  >
                    <span>🚀</span> 开始游戏 ({roomSnapshot.players.length}人就绪)
                  </button>
                ) : (
                  <div className={styles.waitingForHost}>⏳ 等待房主开始游戏...</div>
                )}
                <button className={styles.backBtn} onClick={() => { useRoomStore.getState().reset(); setLobbyMode('home'); }}>
                  🚪 退出房间
                </button>
              </div>
            </>
          )}

          {/* ══════════ 本地就绪确认 ══════════ */}
          {lobbyMode === 'ready' && (
            <>
              <div className={styles.readySection}>
                <h2 className={styles.readyTitle}>✅ 玩家就绪</h2>
                <div className={styles.playerList}>
                  {currentPlayers.map((p, i) => {
                    const char = CHARACTERS.find(c => c.id === p.characterId);
                    return (
                      <div key={p.id} className={styles.playerRow}>
                        <img src={getCharSelectPath(p.characterId)} alt={char?.name} className={styles.playerRowImg} />
                        <span className={styles.playerTag}>{i === 0 ? '👤 你' : '🤖 Bot'}</span>
                        <span className={styles.playerName}>{p.name}</span>
                        <span className={styles.playerChar}>— {char?.name}</span>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.readyActions}>
                  <button className={styles.backBtn} onClick={() => setLobbyMode('offline')}>
                    <span>🔙</span> 返回
                  </button>
                  <button className={styles.startBtn} onClick={handleGoGame}>
                    <span>🚀</span> 进入游戏
                  </button>
                </div>
              </div>
            </>
          )}

        </div>
      </div>

      <div className={styles.footer}>
        {lobbyMode === 'offline' || lobbyMode === 'ready' ? '本地模式 · Hot Seat · V1.0' : 'V1.0 联机版'}
      </div>
    </div>
  );
}
